// HTTP клиент с двойной логикой авторизации
import { API_BASE_URL } from '../config';

// Глобальная переменная для доступа к функции показа модального окна авторизации
let showAuthModal = null;

// Функция для установки ссылки на функцию показа модального окна
export const setAuthModalFunction = (modalFunction) => {
    showAuthModal = modalFunction;
};

class HttpClient {
    constructor() {
        this.isRefreshing = false;
        this.failedQueue = [];
        this.refreshPromise = null;
        this.lastRefreshTime = 0;
        this.minRefreshInterval = 5000;
        this.authErrorCallbacks = [];
        this.hasNotifiedAuthRequired = false;
        this.currentToken = null;
        this.authModalShown = false; // Флаг для отслеживания состояния модального окна
    }

    onAuthError(callback) {
        this.authErrorCallbacks.push(callback);
    }

    offAuthError(callback) {
        const index = this.authErrorCallbacks.indexOf(callback);
        if (index > -1) {
            this.authErrorCallbacks.splice(index, 1);
        }
    }

    notifyAuthError(error) {
        this.authErrorCallbacks.forEach(callback => {
            try {
                callback(error);
            } catch (callbackError) {
                console.error('Error in auth error callback: ', callbackError);
            }
        });
    }

    setToken(token) {
        try {
            if (token) {
                localStorage.setItem('access_token', token);
                this.currentToken = token;
                this.hasNotifiedAuthRequired = false;
            } else {
                localStorage.removeItem('access_token');
                this.currentToken = null;
            }
        } catch (error) {
            console.error('Error setting token:', error);
        }
    }

    processQueue(error, token = null) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });
        this.failedQueue = [];
    }

    // Обновление токена только для браузера (не для Telegram WebApp)
    async refreshToken() {
        try {
            console.log('🔄 Starting access_token refresh for browser...');
            
            // Проверяем, есть ли initData от Telegram
            const hasInitData = !!window.Telegram?.WebApp?.initData;
            
            if (hasInitData) {
                throw new Error('Token refresh not supported in Telegram WebApp - use initData instead');
            }
            
            // Попытка автоматического логина по cookies в браузере
            console.log('🌐 Attempting /auth/auto-login-browser via cookies...');
            const response = await fetch(`${API_BASE_URL}/auth/auto-login-browser`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const error = new Error(errorData?.detail || response.statusText || 'Auto login via cookies failed');
                error.response = { status: response.status, data: errorData };
                throw error;
            }

            const data = await response.json();
            if (data?.access_token) {
                console.log('✅ Auto-login succeeded, token received');
                this.setToken(data.access_token);
                return data.access_token;
            }

            throw new Error('Auto-login did not return access_token');
        } catch (error) {
            console.error('❌ Error refreshing token:', error);
            throw error;
        }
    }

    // Основной метод для выполнения запросов
    async request(url, options = {}) {
        const isTelegramApp = !!window.Telegram?.WebApp;
        const hasInitData = !!window.Telegram?.WebApp?.initData;
        const hasAccessToken = !!localStorage.getItem('access_token');
        
        console.log('🔧 httpClient.request:', {
            url,
            isTelegramApp,
            hasInitData,
            hasAccessToken,
            initDataLength: window.Telegram?.WebApp?.initData?.length || 0
        });
        
        // Исключаем запросы авторизации из проверки токенов
        const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/telegram-auth') || url.includes('/auth/auto-login');
        if (isAuthRequest) {
            console.log('🔐 Auth request detected in request method - skipping token check');
        } else {
            // Проверяем наличие токена авторизации перед выполнением запроса
            if (!hasInitData && !hasAccessToken) {
                console.log('❌ No authorization token available - request blocked');

                if (isTelegramApp) {
                    // В Telegram среде НЕ показываем модалку, initData может появиться позже
                    console.log('📱 Telegram среда без initData — запрос заблокирован, ждём авторизации');
                    const error = new Error('Telegram auth not ready yet');
                    error.code = 'TELEGRAM_AUTH_PENDING';
                    throw error;
                }

                // Показываем модальное окно авторизации только в обычном браузере
                if (showAuthModal && !this.authModalShown) {
                    console.log('🔐 Showing auth modal for unauthorized request');
                    this.authModalShown = true;
                    showAuthModal(true);
                }

                const error = new Error('Authorization required');
                error.code = 'AUTH_REQUIRED';
                if (!this.hasNotifiedAuthRequired) {
                    this.hasNotifiedAuthRequired = true;
                    this.notifyAuthError({ code: 'AUTH_REQUIRED', message: 'No authorization token available' });
                }
                throw error;
            }
        }

        // Убедимся, что объект заголовков существует
        options.headers = { ...(options.headers || {}) };

        // Приоритет: initData от Telegram > access_token
        if (hasInitData) {
            // Если есть initData от Telegram - отправляем его
            options.headers = {
                ...options.headers,
                'X-Telegram-Init-Data': window.Telegram.WebApp.initData,
            };
            console.log('📱 Telegram WebApp: отправляем initData в заголовке X-Telegram-Init-Data');
        } else if (hasAccessToken) {
            // Если нет initData, но есть access_token - используем его
            options.headers = {
                ...options.headers,
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            };
            console.log('🌐 Browser: отправляем access_token в заголовке Authorization');
        } else {
            console.log('⚠️ Нет ни initData, ни access_token - запрос без авторизации');
        }

        console.log('📤 Final headers:', options.headers);

        try {
            // Всегда включаем cookies (нужно для CORS с credentials при авто-логине по cookies)
            const response = await fetch(url, { ...options, credentials: 'include', cache: 'no-store' });
            
            // Обработка 401 только для браузера (когда нет initData)
            if (response.status === 401 && !hasInitData) {
                console.log('🔐 401 Unauthorized in browser - attempting token refresh');
                
                if (this.isRefreshing && this.refreshPromise) {
                    console.log('⏳ Token refresh already in progress, waiting...');
                    try {
                        const newToken = await this.refreshPromise;
                        options.headers['Authorization'] = `Bearer ${newToken}`;
                        return await fetch(url, { ...options, credentials: 'include', cache: 'no-store' });
                    } catch (error) {
                        console.error('❌ Failed to refresh token while waiting:', error);
                        localStorage.removeItem('access_token');
                        if (!this.hasNotifiedAuthRequired) {
                            this.hasNotifiedAuthRequired = true;
                            this.notifyAuthError({ code: 'AUTH_REQUIRED', originalError: error });
                        }
                        throw error;
                    }
                }

                if (!this.isRefreshing) {
                    const now = Date.now();
                    const timeSinceLastRefresh = now - this.lastRefreshTime;
                    
                    if (timeSinceLastRefresh < this.minRefreshInterval) {
                        console.log(`⏰ Too soon to refresh token (${timeSinceLastRefresh}ms < ${this.minRefreshInterval}ms), waiting...`);
                        await new Promise(resolve => setTimeout(resolve, this.minRefreshInterval - timeSinceLastRefresh));
                    }
                    
                    console.log('🚀 Starting new token refresh process');
                    this.isRefreshing = true;
                    this.lastRefreshTime = now;
                    this.refreshPromise = this.refreshToken();
                }

                try {
                    const newToken = await this.refreshPromise;
                    console.log('✅ Token refresh completed, retrying request');
                    
                    options.headers['Authorization'] = `Bearer ${newToken}`;
                    return await fetch(url, { ...options, credentials: 'include', cache: 'no-store' });
                } catch (error) {
                    console.error('❌ Token refresh failed:', error);
                    localStorage.removeItem('access_token');
                    if (!this.hasNotifiedAuthRequired) {
                        this.hasNotifiedAuthRequired = true;
                        this.notifyAuthError({ code: 'AUTH_REQUIRED', originalError: error });
                    }
                    throw error;
                } finally {
                    this.isRefreshing = false;
                    this.refreshPromise = null;
                }
            }

            return response;
        } catch (error) {
            console.error('❌ Request failed:', error);
            throw error;
        }
    }

    async get(url, options = {}) {
        // Проверяем токен перед отправкой запроса
        await this.checkAuthBeforeRequest(url);
        return this.request(url, { ...options, method: 'GET' });
    }

    async post(url, data, options = {}) {
        // Проверяем токен перед отправкой запроса
        await this.checkAuthBeforeRequest(url);
        return this.request(url, {
            ...options,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            body: JSON.stringify(data),
        });
    }

    async put(url, data, options = {}) {
        // Проверяем токен перед отправкой запроса
        await this.checkAuthBeforeRequest(url);
        return this.request(url, {
            ...options,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            body: JSON.stringify(data),
        });
    }

    async delete(url, options = {}) {
        // Проверяем токен перед отправкой запроса
        await this.checkAuthBeforeRequest(url);
        return this.request(url, { ...options, method: 'DELETE' });
    }

    // Метод для проверки авторизации перед запросом
    async checkAuthBeforeRequest(url) {
        // Исключаем запросы авторизации из проверки токенов
        // URL может быть полным (http://localhost:8000/auth/login) или относительным (/auth/login)
        const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/telegram-auth') || url.includes('/auth/auto-login');
        
        if (isAuthRequest) {
            console.log('🔐 Auth request detected - skipping token check for:', url);
            return;
        }
        
        const hasTelegramWebApp = !!window.Telegram?.WebApp;
        const hasValidTelegramData = !!(
            window.Telegram?.WebApp?.initData && 
            window.Telegram?.WebApp?.initData.length > 0 &&
            window.Telegram?.WebApp?.initDataUnsafe?.user?.id
        );
        const hasAccessToken = !!localStorage.getItem('access_token');
        
        console.log('🔐 checkAuthBeforeRequest - ДЕТАЛЬНЫЙ ДЕБАГ:', {
            hasTelegramWebApp,
            hasValidTelegramData,
            hasAccessToken,
            initData: window.Telegram?.WebApp?.initData || 'undefined',
            initDataLength: window.Telegram?.WebApp?.initData?.length || 0,
            initDataUnsafe: window.Telegram?.WebApp?.initDataUnsafe || 'undefined',
            userId: window.Telegram?.WebApp?.initDataUnsafe?.user?.id || 'undefined',
            localStorageAccessToken: localStorage.getItem('access_token') || 'undefined',
            url: url
        });
        
        // Если нет токена и это НЕ Telegram среда вообще, показываем модальное окно
        // Если это Telegram среда — ждём, initData может ещё не загрузиться
        if (!hasValidTelegramData && !hasAccessToken) {
            if (hasTelegramWebApp) {
                // В Telegram среде НЕ показываем модалку и НЕ бросаем ошибку
                // initData может появиться позже, нужно подождать
                console.log('🔐 Telegram среда без initData — ждём загрузки, пропускаем запрос');
                const error = new Error('Telegram auth not ready yet');
                error.code = 'TELEGRAM_AUTH_PENDING';
                throw error;
            }

            console.log('🔐 No valid auth token - showing modal before request (browser)');

            // Уведомляем о необходимости авторизации через событие
            this.notifyAuthError({
                code: 'AUTH_REQUIRED',
                message: 'Authorization required - no valid token available'
            });

            // Бросаем ошибку чтобы остановить выполнение запроса
            const error = new Error('Authorization required');
            error.code = 'AUTH_REQUIRED';
            throw error;
        }
        
        console.log('✅ Auth check passed - proceeding with request');
    }

    // Метод для сброса флага модального окна при навигации
    resetAuthModalFlag() {
        console.log('🔐 Resetting auth modal flag for navigation');
        this.authModalShown = false;
    }

    resetRefreshState() {
        console.log('🔄 Resetting token refresh state');
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.lastRefreshTime = 0;
        this.hasNotifiedAuthRequired = false;
        this.authModalShown = false; // Сбрасываем флаг модального окна
    }
}

export const httpClient = new HttpClient(); 